import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Icon } from '../../lib/icons';
import { BottomSheet } from '../BottomSheet';
import { useMediaQuery } from '../../lib/useMediaQuery';
import { isInToastLayer } from '../../lib/toastLayer';
import { useActivityStore, selectActiveCount, selectVisibleActivities } from '../../lib/activityStore';
import { ActivityRow } from './ActivityRow';
import { readerLocale } from '../../lib/locale';

/**
 * The Activity Center surface, mounted once at the app root.
 *  · Desktop: an anchored liquid-glass tray dropping from the top-right
 *    indicator, portaled to <body> (so no topbar/transform ancestor can clip or
 *    trap it). Closes on outside-click + Esc.
 *  · Mobile: a BottomSheet.
 * Header shows the active count + a "Clear finished" button. Footer links to the
 * historical /activity page and closes the tray. Body maps activities → rows.
 */
export function ActivityCenter() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const reduceMotion = useReducedMotion();
  const panelOpen = useActivityStore((s) => s.panelOpen);
  const setPanelOpen = useActivityStore((s) => s.setPanelOpen);
  // Stable, sorted, de-duped list (running first, then recently-finished). Select
  // the raw array (a stable snapshot ref) and derive via useMemo — calling the
  // selector directly in useActivityStore() returns a fresh array each render and
  // loops React (getSnapshot must be cached).
  const activities = useActivityStore((s) => s.activities);
  const visible = useMemo(() => selectVisibleActivities(activities), [activities]);
  const activeCount = useActivityStore(selectActiveCount);
  const clearFinished = useActivityStore((s) => s.clearFinished);

  const close = () => setPanelOpen(false);
  const hasFinished = visible.some((a) => a.status !== 'running');

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <span style={{ fontWeight: 600, fontSize: 'var(--text-base)' }}>Activity</span>
      {activeCount > 0 && (
        <span
          style={{
            fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999,
            color: 'var(--accent-primary)', background: 'var(--accent-primary-soft)',
          }}
        >
          {activeCount.toLocaleString(readerLocale())} running
        </span>
      )}
      <span style={{ flex: 1 }} />
      {hasFinished && (
        <button
          type="button"
          onClick={clearFinished}
          style={{
            background: 'transparent', border: 'none', color: 'var(--text-tertiary)',
            fontSize: 'var(--text-xs)', cursor: 'pointer', padding: '2px 6px',
          }}
        >
          Clear finished
        </button>
      )}
    </div>
  );

  const body =
    visible.length === 0 ? (
      <div
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 'var(--space-2)', padding: 'var(--space-6) var(--space-4)', color: 'var(--text-tertiary)',
        }}
      >
        <Icon name="satellite" size={24} style={{ color: 'var(--text-tertiary)' }} />
        <span style={{ fontSize: 'var(--text-sm)' }}>Nothing running right now</span>
      </div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Each row fades + collapses on exit so a finished activity (kept 10s by
            the store's AUTO_DISMISS_MS) smoothly disappears. `layout` keeps the
            remaining rows sliding into place. Reduced motion → no transition. */}
        <AnimatePresence initial={false}>
          {visible.map((a) => (
            <motion.div
              key={a.id}
              layout={!reduceMotion}
              initial={reduceMotion ? false : { opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
              transition={reduceMotion ? { duration: 0 } : { duration: 0.26, ease: 'easeInOut' }}
              style={{ overflow: 'hidden' }}
            >
              <ActivityRow activity={a} onNavigate={close} />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    );

  const footer = (
    <Link
      to="/activity"
      onClick={close}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-sm)',
        color: 'var(--accent-primary)', textDecoration: 'none',
      }}
    >
      View all activity
      <Icon name="chevronRight" size={14} />
    </Link>
  );

  // ── Mobile: BottomSheet ────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <BottomSheet
        open={panelOpen}
        onClose={close}
        title={header}
        padded={false}
        footer={footer}
        resizable
      >
        {body}
      </BottomSheet>
    );
  }

  // ── Desktop: anchored liquid-glass tray ────────────────────────────────────
  return <DesktopTray open={panelOpen} onClose={close} header={header} body={body} footer={footer} />;
}

function DesktopTray({
  open,
  onClose,
  header,
  body,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  header: React.ReactNode;
  body: React.ReactNode;
  footer: React.ReactNode;
}) {
  const trayRef = useRef<HTMLDivElement>(null);

  // Outside-click + Esc to close (capture phase so it fires before row buttons).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (trayRef.current && t && !trayRef.current.contains(t)) {
        // Ignore clicks on the indicator itself (it toggles separately), and on a
        // toast — the layer over this tray, often reporting what a row just did;
        // dismissing it is no request to close the tray (b-02055).
        const onIndicator = t instanceof Element && t.closest('.activity-indicator');
        if (!onIndicator && !isInToastLayer(t)) onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          ref={trayRef}
          className="glass-card-static activity-tray"
          role="dialog"
          aria-label="Activity Center"
          initial={{ opacity: 0, scale: 0.96, y: -8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: -8 }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
          style={{
            position: 'fixed',
            top: 'calc(var(--topbar-height, 52px) + var(--space-2))',
            right: 'var(--space-4)',
            width: 380,
            maxWidth: 'calc(100vw - 2 * var(--space-4))',
            maxHeight: 'calc(100dvh - var(--topbar-height, 52px) - 2 * var(--space-4))',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 'var(--z-dropdown)',
            boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: 'var(--space-3) var(--space-4)',
              borderBottom: '1px solid var(--border-subtle)',
              background: 'var(--accent-primary-soft)',
            }}
          >
            {header}
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{body}</div>
          <div
            style={{
              padding: 'var(--space-3) var(--space-4)',
              borderTop: '1px solid var(--border-subtle)',
              background: 'var(--bg-elevated)',
            }}
          >
            {footer}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
